import { useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { displayToastError } from '@/helpers/display-toast';
import { AssignmentModal } from '@/modules/planner/components/modals/assignment-modal/assignment-modal';
import { getProjectDetails } from '@/modules/projects/api/projects.api';
import { getTeamMemberDetails } from '@/modules/team/api/team.api';
import { AuthenticatedRoutes } from '@/router/routes';

import { NOTIFICATION_TYPE_CONFIG } from '../../constants/notification-type-config';
import { Notification } from '../../types';
import { NotificationItemShell } from './notification-item-shell';

interface EntityLinkProps {
  onClick: (e: React.MouseEvent) => void;
  disabled: boolean;
  children?: React.ReactNode;
}

/**
 * Inline name inside the notification copy. A button rather than an anchor because the
 * target is verified before navigating — see `ensureExists`.
 */
function EntityLink({ onClick, disabled, children }: EntityLinkProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="font-medium text-foreground underline underline-offset-2 hover:text-primary disabled:no-underline disabled:opacity-70"
    >
      {children}
    </button>
  );
}

interface NotificationItemUnassignedTimeProps {
  notification: Notification;
  onMarkAsRead?: (notificationId: number) => void;
  onDelete?: (notificationId: number) => void;
}

export function NotificationItemUnassignedTime({
  notification,
  onMarkAsRead,
  onDelete,
}: NotificationItemUnassignedTimeProps) {
  const { t } = useTranslation('authenticated.notifications');
  const navigate = useNavigate();
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [isResolving, setIsResolving] = useState(false);

  const typeConfig = NOTIFICATION_TYPE_CONFIG[notification.type];
  const title = typeConfig ? t(typeConfig.titleKey, notification.args ?? {}) : '';

  const actionArgs = notification.actionArgs ?? {};
  const memberId = actionArgs.memberId;
  const projectId = actionArgs.projectId;

  // The assignment modal renders the raw id in its member/project selectors unless it
  // also receives the display names, so pass the names the notification already carries.
  const memberName = notification.args?.memberName;
  const projectName = notification.args?.projectName;

  const markRead = (): void => {
    if (!notification.isRead && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
  };

  /**
   * The referenced member or project may have been deleted since the notification was sent,
   * and both destinations render an endless spinner for a missing id. Confirm the entity is
   * still there before navigating or opening the modal, and fall back to a toast if it is gone.
   */
  const ensureExists = async (fetchEntity: () => Promise<unknown>, missingKey: string) => {
    setIsResolving(true);
    try {
      await fetchEntity();
      return true;
    } catch {
      displayToastError(t(missingKey));
      return false;
    } finally {
      setIsResolving(false);
    }
  };

  const openMember = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (!memberId) return;

    const exists = await ensureExists(
      () => getTeamMemberDetails(memberId),
      'types.timeTrackedWithoutAssignment.memberUnavailable'
    );
    if (!exists) return;

    markRead();
    navigate(AuthenticatedRoutes.TeamMemberDetails.replace(':memberId', memberId));
  };

  const openProject = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (!projectId) return;

    const exists = await ensureExists(
      () => getProjectDetails(Number(projectId)),
      'types.timeTrackedWithoutAssignment.projectUnavailable'
    );
    if (!exists) return;

    markRead();
    navigate(AuthenticatedRoutes.ProjectDetails.replace(':projectId', projectId));
  };

  const openAssignmentModal = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (!projectId) return;

    const exists = await ensureExists(
      () => getProjectDetails(Number(projectId)),
      'types.timeTrackedWithoutAssignment.projectUnavailable'
    );
    if (!exists) return;

    markRead();
    setIsAssignmentModalOpen(true);
  };

  return (
    <>
      <NotificationItemShell
        notification={notification}
        title={title}
        actionUrl={null}
        onMarkAsRead={onMarkAsRead}
        onDelete={onDelete}
      >
        <p className="whitespace-pre-line text-sm leading-snug text-muted-foreground">
          <Trans
            t={t}
            i18nKey="types.timeTrackedWithoutAssignment.description"
            values={notification.args ?? {}}
            components={{
              memberLink: memberId ? (
                <EntityLink onClick={openMember} disabled={isResolving} />
              ) : (
                <span />
              ),
              projectLink: projectId ? (
                <EntityLink onClick={openProject} disabled={isResolving} />
              ) : (
                <span />
              ),
            }}
          />
        </p>
        <div className="mt-1 flex gap-2">
          {memberId && (
            <Button variant="outline" size="sm" onClick={openMember} disabled={isResolving}>
              {t('types.timeTrackedWithoutAssignment.viewProfile')}
            </Button>
          )}
          {projectId && (
            <Button
              variant="default"
              size="sm"
              onClick={openAssignmentModal}
              disabled={isResolving}
            >
              {t('types.timeTrackedWithoutAssignment.createAssignment')}
            </Button>
          )}
        </div>
      </NotificationItemShell>
      {projectId && (
        <AssignmentModal
          open={isAssignmentModalOpen}
          onClose={() => setIsAssignmentModalOpen(false)}
          initialData={{
            userId: memberId,
            projectId,
            userName: memberName,
            projectLabel: projectName,
          }}
        />
      )}
    </>
  );
}
